/**
 * User Management Service
 * Enterprise-grade API client for user management operations.
 * 
 * Features:
 * - User CRUD operations
 * - Invitation management
 * - Session management
 * - Login history
 * - Statistics
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import api from './api';
import { formatTimeAgoIST, formatISTDate } from '../utils/timezone';

// User status constants
export const USER_STATUS = {
  ACTIVE: 'active',
  INVITED: 'invited',
  PENDING_ACTIVATION: 'pending_activation',
  LOCKED: 'locked',
  SUSPENDED: 'suspended',
  DEACTIVATED: 'deactivated',
  ARCHIVED: 'archived',
};

// User role constants with metadata
export const USER_ROLES = {
  admin: {
    value: 'admin',
    label: 'Admin',
    description: 'Full system access',
    actionCount: 136,
    color: 'bg-purple-100 text-purple-800',
  },
  designs_manager: {
    value: 'designs_manager',
    label: 'Designs Manager',
    description: 'Parts CRUD, AI, Taxonomy, Revisions',
    actionCount: 89,
    color: 'bg-blue-100 text-blue-800',
  },
  designs_engineer: {
    value: 'designs_engineer',
    label: 'Designs Engineer',
    description: 'Parts Create/Update, AI, Revisions',
    actionCount: 67,
    color: 'bg-cyan-100 text-cyan-800',
  },
  quality_manager: {
    value: 'quality_manager',
    label: 'Quality Manager',
    description: 'Delete/Restore, Release, Audit Admin',
    actionCount: 62,
    color: 'bg-green-100 text-green-800',
  },
  quality_engineer: {
    value: 'quality_engineer',
    label: 'Quality Engineer',
    description: 'Classify, View Audit',
    actionCount: 45,
    color: 'bg-emerald-100 text-emerald-800',
  },
  inventory_manager: {
    value: 'inventory_manager',
    label: 'Inventory Manager',
    description: 'Full Inventory Control',
    actionCount: 42,
    color: 'bg-orange-100 text-orange-800',
  },
  operator: {
    value: 'operator',
    label: 'Operator',
    description: 'Basic Inventory, View Only',
    actionCount: 38,
    color: 'bg-yellow-100 text-yellow-800',
  },
  viewer: {
    value: 'viewer',
    label: 'Viewer',
    description: 'Read-Only Everywhere',
    actionCount: 35,
    color: 'bg-slate-100 text-slate-800',
  },
};

// Status display configuration
export const STATUS_CONFIG = {
  active: {
    label: 'Active',
    color: 'bg-green-100 text-green-800',
    icon: '●',
  },
  invited: {
    label: 'Invited',
    color: 'bg-blue-100 text-blue-800',
    icon: '◐',
  },
  pending_activation: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800',
    icon: '○',
  },
  locked: {
    label: 'Locked',
    color: 'bg-red-100 text-red-800',
    icon: '◉',
  },
  suspended: {
    label: 'Suspended',
    color: 'bg-orange-100 text-orange-800',
    icon: '◉',
  },
  deactivated: {
    label: 'Deactivated',
    color: 'bg-slate-100 text-slate-800',
    icon: '○',
  },
  archived: {
    label: 'Archived',
    color: 'bg-slate-200 text-slate-600',
    icon: '○',
  },
};

class UserService {
  /**
   * Get user statistics for dashboard
   */
  async getStats() {
    const response = await api.get('/api/v1/users/stats');
    return response.data;
  }

  /**
   * List users with filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Offset for pagination
   * @param {number} params.limit - Number of results
   * @param {string} params.status - Filter by status
   * @param {string} params.role - Filter by role
   * @param {string} params.department - Filter by department
   * @param {string} params.search - Search query
   * @param {string} params.sort_by - Sort field
   * @param {string} params.sort_order - Sort direction (asc/desc)
   */
  async listUsers(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.skip !== undefined) queryParams.append('skip', params.skip);
    if (params.limit !== undefined) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.role) queryParams.append('role', params.role);
    if (params.department) queryParams.append('department', params.department);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const response = await api.get(`/api/v1/users?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get user by ID
   * @param {string} userId - User ID
   */
  async getUser(userId) {
    const response = await api.get(`/api/v1/users/${userId}`);
    return response.data;
  }

  /**
   * Update user details
   * @param {string} userId - User ID
   * @param {Object} data - Update data
   */
  async updateUser(userId, data) {
    const response = await api.put(`/api/v1/users/${userId}`, data);
    return response.data;
  }

  /**
   * Invite a new user
   * @param {Object} data - Invitation data
   * @param {string} data.email - User's email
   * @param {string} data.first_name - First name
   * @param {string} data.last_name - Last name
   * @param {string} data.role - Role to assign
   * @param {string} data.department - Department
   * @param {string} data.job_title - Job title
   */
  async inviteUser(data) {
    const response = await api.post('/api/v1/users/invite', data);
    return response.data;
  }

  /**
   * Resend invitation email
   * @param {string} userId - User ID
   */
  async resendInvitation(userId) {
    const response = await api.post(`/api/v1/users/${userId}/resend-invitation`);
    return response.data;
  }

  /**
   * Suspend a user
   * @param {string} userId - User ID
   * @param {string} reason - Reason for suspension
   */
  async suspendUser(userId, reason = null) {
    const response = await api.post(`/api/v1/users/${userId}/suspend`, null, {
      params: { reason },
    });
    return response.data;
  }

  /**
   * Reactivate a suspended/deactivated user
   * @param {string} userId - User ID
   */
  async reactivateUser(userId) {
    const response = await api.post(`/api/v1/users/${userId}/reactivate`);
    return response.data;
  }

  /**
   * Unlock a locked user
   * @param {string} userId - User ID
   */
  async unlockUser(userId) {
    const response = await api.post(`/api/v1/users/${userId}/unlock`);
    return response.data;
  }

  /**
   * Deactivate a user (soft delete)
   * @param {string} userId - User ID
   * @param {string} reason - Reason for deactivation
   */
  async deactivateUser(userId, reason = null) {
    const response = await api.post(`/api/v1/users/${userId}/deactivate`, null, {
      params: { reason },
    });
    return response.data;
  }

  /**
   * Reset user password
   * @param {string} userId - User ID
   * @param {string} mode - 'email' or 'manual'
   * @param {string} newPassword - New password (required for manual mode)
   */
  async resetPassword(userId, mode, newPassword = null) {
    const response = await api.post(`/api/v1/users/${userId}/reset-password`, {
      mode,
      new_password: newPassword,
    });
    return response.data;
  }

  /**
   * Get user sessions (Admin - for viewing other users)
   * @param {string} userId - User ID
   */
  async getUserSessions(userId) {
    const response = await api.get(`/api/v1/users/${userId}/sessions`);
    return response.data;
  }

  /**
   * Get current user's own sessions (Self-service)
   * No admin permission required
   * @param {Object} options - Query options
   * @param {boolean} options.includeHistory - Include session history (default: true)
   * @param {number} options.historyDays - Days of history to fetch (default: 30)
   */
  async getMySessions({ includeHistory = true, historyDays = 30 } = {}) {
    const params = new URLSearchParams();
    params.append('include_history', includeHistory);
    params.append('history_days', historyDays);
    const response = await api.get(`/api/v1/users/me/sessions?${params.toString()}`);
    return response.data;
  }

  /**
   * Terminate one of current user's own sessions
   * @param {string} sessionId - Session ID to terminate
   */
  async terminateMySession(sessionId) {
    const response = await api.delete(`/api/v1/users/me/sessions/${sessionId}`);
    return response.data;
  }

  /**
   * Terminate all current user's sessions except current
   * "Log out from all other devices" feature
   */
  async terminateAllMyOtherSessions() {
    const response = await api.delete('/api/v1/users/me/sessions');
    return response.data;
  }

  /**
   * Force logout all sessions for a user (Admin only)
   * @param {string} userId - User ID
   */
  async forceLogoutAll(userId) {
    const response = await api.delete(`/api/v1/users/${userId}/sessions`);
    return response.data;
  }

  /**
   * Terminate a specific session (Admin only)
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID
   */
  async terminateSession(userId, sessionId) {
    const response = await api.delete(`/api/v1/users/${userId}/sessions/${sessionId}`);
    return response.data;
  }

  /**
   * Get login history for a user
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters
   */
  async getLoginHistory(userId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) queryParams.append('skip', params.skip);
    if (params.limit !== undefined) queryParams.append('limit', params.limit);
    if (params.event_type) queryParams.append('event_type', params.event_type);
    
    const response = await api.get(`/api/v1/users/${userId}/login-history?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get available roles
   */
  async getRoles() {
    const response = await api.get('/api/v1/auth/roles');
    return response.data;
  }
}

// Helper functions
export const getRoleConfig = (role) => {
  return USER_ROLES[role] || USER_ROLES.viewer;
};

export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.active;
};

export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Never';
  
  // Use centralized timezone utility for IST
  const timeAgo = formatTimeAgoIST(dateString);
  if (timeAgo) return timeAgo;
  
  // Fallback to IST date format
  return formatISTDate(dateString);
};

export const generateSecurePassword = (length = 16) => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*()';
  const all = uppercase + lowercase + digits + special;
  
  let password = '';
  // Ensure at least one of each type
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += digits[Math.floor(Math.random() * digits.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  // Fill the rest
  for (let i = password.length; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }
  
  // Shuffle
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

export const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: 'None', color: 'bg-slate-200' };
  
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  
  const levels = [
    { score: 0, label: 'Very Weak', color: 'bg-red-500' },
    { score: 2, label: 'Weak', color: 'bg-orange-500' },
    { score: 3, label: 'Fair', color: 'bg-yellow-500' },
    { score: 4, label: 'Good', color: 'bg-lime-500' },
    { score: 5, label: 'Strong', color: 'bg-green-500' },
    { score: 6, label: 'Very Strong', color: 'bg-emerald-500' },
  ];
  
  const level = levels.filter(l => score >= l.score).pop();
  return { score, ...level };
};

const userService = new UserService();
export default userService;
